import { z } from 'zod';
import { ImageId, MediaPublicReason } from './values.mjs';
import { IMAGE_LIMITS, IMAGE_EDGES } from './limits.mjs';
import { isImageRenditionGeometry } from './geometry.mjs';

const plainText = (max) =>
  z
    .string()
    .max(max)
    .regex(/^[^\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]*$/u);
const dimension = z.int().positive().max(IMAGE_LIMITS.maxDimension);
const sameOriginPath = z
  .string()
  .min(1)
  .max(2048)
  .refine((value) => {
    if (
      !value.startsWith('/') ||
      value.startsWith('//') ||
      /[\\\s\u0000-\u001f\u007f]/u.test(value)
    )
      return false;
    const url = new URL(value, 'https://managed-image.invalid');
    return url.origin === 'https://managed-image.invalid' && !url.hash;
  }, 'image_route_must_be_same_origin');

// RUN issues these paths after current document/version/purpose admission.
// Neither this descriptor nor its hash/ref grants access to raster bytes.
export const ManagedImageVariant = z.strictObject({
  role: z.union([z.literal('master'), z.literal(320), z.literal(640), z.literal(1280)]),
  width: dimension,
  height: dimension,
  mime: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  sha256: z.string().regex(/^[a-f0-9]{64}$/u),
  byteLength: z.int().positive().max(IMAGE_LIMITS.maxBytes),
  src: sameOriginPath,
});
const common = {
  alt: plainText(1000),
  sourceLabel: plainText(200),
  observedAt: z.iso.datetime({ offset: true }).nullable(),
};
export const ManagedImageProjection = z
  .discriminatedUnion('state', [
    z.strictObject({
      ...common,
      state: z.literal('ready'),
      imageId: ImageId,
      width: dimension,
      height: dimension,
      variants: z.array(ManagedImageVariant).length(4),
    }),
    z.strictObject({ ...common, state: z.literal('capturing'), captureOperationRef: ImageId }),
    z.strictObject({ ...common, state: z.literal('missing') }),
    z.strictObject({ ...common, state: z.literal('expired'), imageId: ImageId }),
    z.strictObject({ ...common, state: z.literal('withdrawn'), imageId: ImageId }),
    z.strictObject({ ...common, state: z.literal('refused'), reason: MediaPublicReason }),
  ])
  .superRefine((value, ctx) => {
    if (value.state !== 'ready') return;
    if (value.width * value.height > IMAGE_LIMITS.maxPixels)
      ctx.addIssue({ code: 'custom', message: 'image_too_large' });
    const roles = ['master', ...IMAGE_EDGES];
    const seen = new Map();
    value.variants.forEach((variant, index) => {
      if (variant.role !== roles[index] || !isImageRenditionGeometry(value, variant))
        ctx.addIssue({
          code: 'custom',
          message: 'image_variant_invalid',
          path: ['variants', index],
        });
      const facts = JSON.stringify([
        variant.width,
        variant.height,
        variant.mime,
        variant.byteLength,
      ]);
      if (seen.has(variant.sha256) && seen.get(variant.sha256) !== facts)
        ctx.addIssue({
          code: 'custom',
          message: 'image_variant_digest_conflict',
          path: ['variants', index],
        });
      seen.set(variant.sha256, facts);
    });
  });

// A current reader uses this for foreign/unauthorized refs. No caller echo,
// existence, dimensions, original ref or source provenance escapes refusal.
export function refusedImageProjection() {
  return ManagedImageProjection.parse({
    state: 'refused',
    reason: 'policy_refused',
    alt: '',
    sourceLabel: '',
    observedAt: null,
  });
}
